import { Component, Input, OnDestroy, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { Subscription } from 'rxjs';
import { GeneratorService } from 'src/app/deebr/service/generator.service';
import { ToastService } from 'src/app/deebr/service/toast.service';
import { DynamicSyncService, FirestoreSyncStatus } from 'src/app/deebr/service/dynamic-sync.service';
import { DownloadService } from 'src/app/deebr/service/download.service';

@Component({
    selector: 'app-dynamic-image-management',
    templateUrl: './dynamic-image-management.component.html',
    styleUrls: ['./dynamic-image-management.component.scss'],
    standalone: false
})
export class DynamicImageManagementComponent implements OnInit, OnDestroy {
    @Input() emailId!: number;

    images: any[] = [];
    loading = true;
    firestoreStatus: FirestoreSyncStatus = 'pending';
    totalPersonalizedUsers: number = 0;
    exportingPersonalizableUsers = false;
    // Filtre par segment email (libellés de la matrice de segmentation) appliqué au compteur et au CSV
    segmentOptions: { label: string, count: number }[] = [];
    selectedSegments: string[] = [];
    loadingSegments = false;
    private segmentsLoaded = false;
    private syncSub?: Subscription;

    get displayedUserCount(): number {
        if (this.selectedSegments.length === 0) return this.totalPersonalizedUsers;
        return this.segmentOptions
            .filter(s => this.selectedSegments.includes(s.label))
            .reduce((sum, s) => sum + s.count, 0);
    }

    get baseImages(): any[] {
        return this.images.slice(0, this.slotCount);
    }

    get reserveImages(): any[] {
        return this.images.slice(this.slotCount);
    }

    _selectedSlotTabIndex: number = 1; // Default to Slot 1

    get selectedSlotTabIndex(): number {
        return this._selectedSlotTabIndex;
    }

    set selectedSlotTabIndex(val: number) {
        this._selectedSlotTabIndex = val;
        this.updateDisplayImages();
    }

    get slotTabs(): number[] {
        return Array(this.slotCount).fill(0).map((x, i) => i + 1);
    }

    baseImagesWithSlots: any[] = [];
    reserveImagesForSelectedSlot: any[] = [];

    updateDisplayImages() {
        this.baseImagesWithSlots = this.baseImages.map((img, index) => {
            // Pour les slots de BASE, le numéro de slot est toujours l'index ordinal (position dans baseImages).
            // On ne prend PAS Math.min(slotKeys) car un produit de base peut aussi figurer dans
            // le pool dynamique d'autres slots → Math.min retournerait toujours 1.
            const slotIdx = index + 1;
            const slotData = img.slots ? img.slots[slotIdx.toString()] : null;
            const userCount = img.user_counts_per_slot ? img.user_counts_per_slot[slotIdx.toString()] : img.user_count;
            return {
                ...img,
                slot_number: slotIdx,
                display_url_desk: slotData ? slotData.url_desk : (img.url_desk || img.url),
                display_url_mob: slotData ? slotData.url_mob : (img.url_mob || img.url),
                display_user_count: userCount
            };
        });

        this.reserveImagesForSelectedSlot = this.reserveImages
            .filter(img => {
                let slotIdx = this._selectedSlotTabIndex;
                if (!img.slots || !img.slots[slotIdx.toString()]) return false;
                // Mutualisation : un produit peut être généré pour un slot sans avoir
                // d'users ciblés dessus. On masque ces aperçus vides.
                const userCount = img.user_counts_per_slot ? img.user_counts_per_slot[slotIdx.toString()] : img.user_count;
                return (userCount || 0) > 0;
            })
            .map(img => {
                let slotIdx = this._selectedSlotTabIndex;
                let slotData = img.slots ? img.slots[slotIdx.toString()] : null;
                let userCount = img.user_counts_per_slot ? img.user_counts_per_slot[slotIdx.toString()] : img.user_count;
                return {
                    ...img,
                    display_url_desk: slotData ? slotData.url_desk : (img.url_desk || img.url),
                    display_url_mob: slotData ? slotData.url_mob : (img.url_mob || img.url),
                    display_user_count: userCount
                };
            })
            .sort((a, b) => (b.display_user_count || 0) - (a.display_user_count || 0));
    }

    slotCount = 0;
    selectedImages: Set<string> = new Set();
    isSelectionMode = false;
    viewMode: 'desk' | 'mob' = 'desk';

    @ViewChild('deleteDialog') deleteDialog!: TemplateRef<any>;
    @ViewChild('previewDialog') previewDialog!: TemplateRef<any>;
    previewUrl: string = '';

    openPreview(url: string) {
        this.previewUrl = url;
        this.dialogService.open(this.previewDialog);
    }

    constructor(
        protected ref: NbDialogRef<DynamicImageManagementComponent>,
        private generatorService: GeneratorService,
        private toastService: ToastService,
        private dialogService: NbDialogService,
        private syncService: DynamicSyncService,
        private downloadService: DownloadService,
    ) { }

    ngOnInit(): void {
        this.loadImages();
        this.syncSub = this.syncService.getStatus$(this.emailId).subscribe(status => {
            const wasSyncing = this.firestoreStatus === 'syncing';
            this.firestoreStatus = status;
            if (wasSyncing && status === 'ready') {
                this.toastService.success('Synchronisation terminée', 'Les aperçus reflètent maintenant les changements.');
                this.segmentsLoaded = false;
                this.loadImages();
            } else if (wasSyncing && status === 'error') {
                this.toastService.error('Erreur de synchronisation', 'Veuillez contacter votre chargé de compte.');
            }
        });
    }

    ngOnDestroy(): void {
        this.syncSub?.unsubscribe();
    }

    get deleteButtonTooltip(): string {
        if (this.firestoreStatus === 'syncing') return 'Synchronisation en cours, réessayez dans ~2 min';
        if (this.firestoreStatus === 'error') return 'Erreur de synchronisation, veuillez contacter votre chargé de compte';
        return '';
    }

    loadImages() {
        this.loading = true;
        this.generatorService.getDynamicImages(this.emailId).subscribe({
            next: (response: any) => {
                if (response.data) {
                    this.images = response.data;
                    this.slotCount = response.slot_count || 4;
                    this.totalPersonalizedUsers = response.total_personalized_users || 0;
                    // Si la sync est encore en cours côté back (cas : user rouvre la popin
                    // pendant une propagation), relance le polling via le service.
                    const backStatus = response.firestore_status || 'pending';
                    if (backStatus === 'syncing' && this.syncService.getCurrentStatus(this.emailId) !== 'syncing') {
                        this.syncService.startSync(this.emailId);
                    } else {
                        this.syncService.setStatus(this.emailId, backStatus);
                    }
                    if (backStatus === 'ready') {
                        this.loadSegmentCounts();
                    }
                } else {
                    this.images = response;
                    this.slotCount = 4;
                }
                this.updateDisplayImages();
                this.loading = false;
            },
            error: (err) => {
                console.error(err);
                this.toastService.error('Erreur', 'Impossible de charger les images');
                this.loading = false;
            }
        });
    }

    loadSegmentCounts() {
        if (this.segmentsLoaded || this.loadingSegments) return;
        this.loadingSegments = true;
        this.generatorService.getPersonalizableSegmentCounts(this.emailId).subscribe({
            next: (response) => {
                this.segmentOptions = response.segments || [];
                this.selectedSegments = this.selectedSegments.filter(label => this.segmentOptions.some(s => s.label === label));
                this.totalPersonalizedUsers = response.total;
                this.segmentsLoaded = true;
                this.loadingSegments = false;
            },
            error: (err) => {
                // Sans répartition par segment, le sélecteur reste masqué : l'export complet fonctionne toujours.
                console.error(err);
                this.loadingSegments = false;
            }
        });
    }

    toggleSelection(id: string) {
        if (this.selectedImages.has(id)) {
            this.selectedImages.delete(id);
        } else {
            this.selectedImages.add(id);
        }
    }

    deleteSelected() {
        if (this.selectedImages.size === 0) return;
        if (this.firestoreStatus !== 'ready') {
            this.toastService.error('Non disponible', 'Impossible de supprimer : la personnalisation Firestore est encore en cours de chargement.');
            return;
        }
        this.dialogService.open(this.deleteDialog);
    }

    confirmDelete(ref: any) {
        ref.close();
        this.loading = true;
        const idsToDelete = Array.from(this.selectedImages);

        this.generatorService.deleteDynamicImages(this.emailId, idsToDelete).subscribe({
            next: () => {
                this.toastService.success('Suppression lancée', 'Synchronisation en cours (~2 min)');
                this.images = this.images.filter(img => !this.selectedImages.has(img.id));
                this.selectedImages.clear();
                this.isSelectionMode = false;
                this.updateDisplayImages();
                this.loading = false;
                this.syncService.startSync(this.emailId);
            },
            error: (err) => {
                console.error(err);
                this.toastService.error('Erreur', 'Impossible de supprimer les images');
                this.loading = false;
            }
        });
    }



    exportPersonalizableUsers() {
        if (this.exportingPersonalizableUsers) return;
        this.exportingPersonalizableUsers = true;
        this.toastService.success('Génération en cours', 'Scan des shards GCS + filtre CRM (~10-30s)…');

        this.generatorService.exportPersonalizableUsers(this.emailId, this.selectedSegments).subscribe({
            next: (response) => {
                const blob = response.body;
                if (!blob) {
                    this.toastService.error('Erreur', 'Réponse vide du serveur');
                    this.exportingPersonalizableUsers = false;
                    return;
                }

                const disposition = response.headers.get('Content-Disposition') || '';
                const match = /filename="?([^"]+)"?/.exec(disposition);
                const filename = match ? match[1] : `personalizable_users_${this.emailId}.csv`;

                this.downloadService.saveAs(blob, filename);

                const countHeader = response.headers.get('X-Personalizable-Count');
                const count = countHeader ? parseInt(countHeader, 10) : null;
                if (count !== null && !isNaN(count)) {
                    this.toastService.success('Export terminé', `${count.toLocaleString('fr-FR')} users personnalisables exportés.`);
                } else {
                    this.toastService.success('Export terminé', 'CSV téléchargé.');
                }
                this.exportingPersonalizableUsers = false;
            },
            error: (err) => {
                console.error(err);
                this.toastService.error('Erreur export', err?.error?.error || 'Impossible de générer le CSV');
                this.exportingPersonalizableUsers = false;
            }
        });
    }

    close() {
        this.ref.close();
    }
}
